import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { EditorContent, Extension, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import Image from "@tiptap/extension-image";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Columns3,
  GripVertical,
  Heading1,
  Heading2,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Plus,
  Redo2,
  Rows3,
  Search,
  Strikethrough,
  Table2,
  Trash2,
  Underline as UnderlineIcon,
  Undo2,
  Variable,
} from "lucide-react";
import { classNames, cleanTemplateName } from "../lib/format";

const VARIABLES = [
  ["员工姓名", "{{staffName}}"],
  ["员工编号", "{{staffCode}}"],
  ["身份证号", "{{idNum}}"],
  ["出生日期", "{{birthDate}}"],
  ["证件类型", "{{idType}}"],
  ["性别", "{{gender}}"],
  ["民族", "{{ethnicity}}"],
  ["籍贯", "{{nativePlace}}"],
  ["身体状况", "{{physicalCondition}}"],
  ["户籍地址", "{{householdAddress}}"],
  ["户口类型", "{{householdType}}"],
  ["居住地址", "{{residentialAddress}}"],
  ["员工电话", "{{phone}}"],
  ["通讯地址", "{{employeeAddress}}"],
  ["电子邮箱", "{{email}}"],
  ["微信号", "{{wechatId}}"],
  ["紧急联系人姓名", "{{emergencyContactName}}"],
  ["紧急联系人关系", "{{emergencyContactRelation}}"],
  ["紧急联系人电话", "{{emergencyContactPhone}}"],
  ["职位/工作岗位", "{{position}}"],
  ["公司名称", "{{companyName}}"],
  ["统一信用代码", "{{companyCreditCode}}"],
  ["法定代表人", "{{legalRepresentative}}"],
  ["企业电话", "{{companyPhone}}"],
  ["企业地址", "{{companyAddress}}"],
  ["合同编号", "{{contractNo}}"],
  ["合同签订日期", "{{contractSignDate}}"],
  ["合同开始日期", "{{contractStartDate}}"],
  ["合同结束日期", "{{contractEndDate}}"],
  ["合同执行状态", "{{contractExecutionStatus}}"],
  ["合同类型", "{{contractType}}"],
  ["合同期限", "{{contractTerm}}"],
  ["试用期", "{{probationPeriod}}"],
  ["保密类型", "{{confidentialityType}}"],
  ["保密结束日期", "{{confidentialityEndDate}}"],
  ["旷工天数", "{{absenteeismDays}}"],
  ["入职日期", "{{onboardingDate}}"],
  ["入职部门", "{{onboardingDepartment}}"],
  ["工种及熟练程度", "{{workTypeSkillLevel}}"],
  ["工作内容", "{{workContent}}"],
  ["工作地点", "{{workLocation}}"],
  ["工时制度", "{{workHourSystem}}"],
  ["工伤保险", "{{workInjuryInsurance}}"],
  ["支付方式", "{{paymentMethod}}"],
  ["劳动合同/劳务协议", "{{laborOrServiceContract}}"],
  ["劳动关系/劳务关系", "{{laborOrServiceRelation}}"],
  ["劳动报酬/劳务报酬", "{{laborOrServiceCompensation}}"],
  ["周期类型", "{{periodType}}"],
  ["支付费用", "{{paymentFee}}"],
  ["甲方每月支付", "{{employerMonthlyPayment}}"],
  ["工资方案", "{{salaryPlan}}"],
  ["计件单价", "{{pieceUnitPrice}}"],
  ["基本工资", "{{baseSalary}}"],
  ["试用期工资", "{{probationSalary}}"],
  ["转正工资", "{{regularSalary}}"],
  ["支付日期", "{{paymentDate}}"],
  ["年休假工资标准", "{{annualLeaveSalaryStandard}}"],
  ["病假工资标准", "{{sickLeaveSalaryStandard}}"],
  ["事假工资标准", "{{personalLeaveSalaryStandard}}"],
  ["加班工资标准", "{{overtimeSalaryStandard}}"],
  ["是否选择试用期", "{{hasProbation}}"],
  ["试用期开始日期", "{{probationStartDate}}"],
  ["试用期结束日期", "{{probationEndDate}}"],
  ["解除合同日期", "{{contractReleaseDate}}"],
  ["辞职申请日期", "{{resignationApplicationDate}}"],
  ["实际离职日期", "{{actualResignationDate}}"],
  ["当前日期", "{{currentDate}}"],
];

const TABLE_LABEL_VARIABLE_RULES = [
  ["姓名", "staffName"],
  ["员工姓名", "staffName"],
  ["手机", "phone"],
  ["手机号码", "phone"],
  ["手机号", "phone"],
  ["员工电话", "phone"],
  ["性别", "gender"],
  ["出生日期", "birthDate"],
  ["生日日期", "birthDate"],
  ["邮箱", "email"],
  ["电子邮箱", "email"],
  ["籍贯", "nativePlace"],
  ["民族", "ethnicity"],
  ["身份证号", "idNum"],
  ["身份证号码", "idNum"],
  ["证件号", "idNum"],
  ["证件号码", "idNum"],
  ["证件类型", "idType"],
  ["户口类型", "householdType"],
  ["户籍地址", "householdAddress"],
  ["居住地址", "residentialAddress"],
  ["通讯地址", "employeeAddress"],
  ["紧急联系人姓名", "emergencyContactName"],
  ["紧急联系人关系", "emergencyContactRelation"],
  ["紧急联系人电话", "emergencyContactPhone"],
];

const TEXT_ALIGN_VALUES = ["left", "center", "right"];
const A4_HEIGHT_RATIO = 297 / 210;
const DEFAULT_EDITOR_PAGE_WIDTH = 840;
const DEFAULT_EDITOR_PAGE_HEIGHT = DEFAULT_EDITOR_PAGE_WIDTH * A4_HEIGHT_RATIO;
const DEFAULT_EDITOR_PAGE_GAP = 20;
// 自动分页保存为正式分页后仍使用同一页边距，重开时不得再增加28px造成二次换页。
const HARD_PAGE_BREAK_TOP_GAP = 0;
const PAGE_BREAK_CLASS = "template-editor-page-break-before";
const TABLE_PAGE_BREAK_CLASS = "template-editor-table-page-break";
const SERIALIZED_PAGE_BREAK_CLASS = "template-content-page-break-before";
const SERIALIZED_PAGE_BREAK_NODE_TYPES = new Set(["heading", "paragraph", "table", "tableRow"]);
const PAGINATION_PLUGIN_KEY = new PluginKey("templateEditorPagination");

function positivePageCount(value) {
  return Math.max(1, Math.round(Number(value) || 1));
}

function initialPagination(pageCount) {
  const count = positivePageCount(pageCount);
  return {
    count,
    height: count * DEFAULT_EDITOR_PAGE_HEIGHT + (count - 1) * DEFAULT_EDITOR_PAGE_GAP,
    breaks: [],
  };
}

function samePageBreaks(first = [], second = []) {
  if (first.length !== second.length) return false;
  return first.every((pageBreak, index) => {
    const other = second[index];
    return other
      && pageBreak.kind === other.kind
      && pageBreak.position === other.position;
  });
}

function isShortHeadingLeadIn(block) {
  const text = String(block?.text || "").replace(/\s+/g, "");
  return block?.type === "paragraph" && text.length > 0 && text.length <= 80 && block.height <= 96;
}

function headingGroupBreakTarget(blocks, index, addedOffset, pageContentTop) {
  const block = blocks[index];
  if (block?.type !== "table") return block;
  const heading = blocks[index - 1];
  if (heading?.type !== "heading") return block;
  const leadIn = isShortHeadingLeadIn(blocks[index - 2]) ? blocks[index - 2] : heading;
  const targetTop = leadIn.top + addedOffset;
  return targetTop > pageContentTop + 0.5 ? leadIn : block;
}

function hardPageBreakGroupBottom(blocks, index, addedOffset) {
  const block = blocks[index];
  let bottom = (block?.bottom || 0) + addedOffset;
  if (block?.type !== "heading") return bottom;
  for (let cursor = index + 1; cursor < blocks.length; cursor += 1) {
    const next = blocks[cursor];
    if (!next || next.pageBreakBefore) break;
    if (!String(next.text || "").trim()) continue;
    bottom = Math.max(bottom, next.bottom + addedOffset);
    break;
  }
  return bottom;
}

function hardPageBreakTargetPageIndex({
  blocks,
  index,
  pageIndex,
  pageStride,
  pageHeight,
  paddingTop,
  paddingBottom,
  adjustedTop,
  breakTop,
  addedOffset,
}) {
  const blockNaturalPageIndex = Math.max(pageIndex, Math.floor(adjustedTop / pageStride));
  let targetPageIndex = blockNaturalPageIndex > pageIndex ? blockNaturalPageIndex : pageIndex + 1;
  const groupBottom = hardPageBreakGroupBottom(blocks, index, addedOffset);
  while (
    groupBottom + Math.max(0, targetPageIndex * pageStride + paddingTop + HARD_PAGE_BREAK_TOP_GAP - breakTop)
    > targetPageIndex * pageStride + pageHeight - paddingBottom + 0.5
  ) {
    targetPageIndex += 1;
  }
  return targetPageIndex;
}

function proseMirrorNodeSize(node) {
  if (!node || typeof node !== "object") return 0;
  if (node.type === "text") return String(node.text || "").length;
  if (["image", "hardBreak", "horizontalRule"].includes(node.type)) return 1;
  // 空段落仍占首尾两个位置，按叶节点计为1会使后续所有分页位置错位。
  return 2 + (node.content || []).reduce((total, child) => total + proseMirrorNodeSize(child), 0);
}

function setNodePageBreak(node, enabled) {
  if (!node || typeof node !== "object") return;
  const attrs = { ...(node.attrs || {}) };
  if (enabled) attrs.pageBreakBefore = true;
  else delete attrs.pageBreakBefore;
  if (Object.keys(attrs).length) node.attrs = attrs;
  else delete node.attrs;
}

function withSerializedPageBreaks(documentJson, pageBreaks = []) {
  if (!documentJson) return documentJson;
  const next = JSON.parse(JSON.stringify(documentJson));
  if (!Array.isArray(next.content)) return next;
  const breakPositions = new Set(pageBreaks.map((pageBreak) => pageBreak.position));
  const serializeChildren = (parent, contentStart) => {
    let offset = 0;
    (parent.content || []).forEach((node) => {
      const position = contentStart + offset;
      if (SERIALIZED_PAGE_BREAK_NODE_TYPES.has(node.type)) {
        // 显式分页属于正文，布局尚未完成时也不能被空的测量结果清除。
        setNodePageBreak(node, Boolean(node.attrs?.pageBreakBefore) || breakPositions.has(position));
      }
      if (Array.isArray(node.content)) serializeChildren(node, position + 1);
      offset += proseMirrorNodeSize(node);
    });
  };
  serializeChildren(next, 0);
  return next;
}

const PaginationDecorations = Extension.create({
  name: "templateEditorPagination",
  addProseMirrorPlugins() {
    return [new Plugin({
      key: PAGINATION_PLUGIN_KEY,
      state: {
        init: () => DecorationSet.empty,
        apply(transaction, currentDecorations) {
          const pagination = transaction.getMeta(PAGINATION_PLUGIN_KEY);
          if (pagination) {
            const decorations = (pagination.breaks || []).flatMap((pageBreak) => {
              if (pageBreak.kind === "tableRow") {
                const row = transaction.doc.nodeAt(pageBreak.position);
                if (!row?.isBlock) return [];
                return [Decoration.node(
                  pageBreak.position,
                  pageBreak.position + row.nodeSize,
                  {
                    class: TABLE_PAGE_BREAK_CLASS,
                    style: [
                      `--template-table-page-break-height: ${pageBreak.height}px`,
                      `--template-table-page-gap-start: ${pageBreak.gapStart}px`,
                      `--template-table-page-gap: ${pageBreak.pageGap}px`,
                    ].join("; "),
                  },
                )];
              }
              const node = transaction.doc.nodeAt(pageBreak.position);
              if (!node?.isBlock) return [];
              return [Decoration.node(
                pageBreak.position,
                pageBreak.position + node.nodeSize,
                {
                  class: PAGE_BREAK_CLASS,
                  style: `margin-top: ${pageBreak.marginTop}px !important`,
                },
              )];
            });
            return DecorationSet.create(transaction.doc, decorations);
          }
          return transaction.docChanged
            ? currentDecorations.map(transaction.mapping, transaction.doc)
            : currentDecorations;
        },
      },
      props: {
        decorations(state) {
          return PAGINATION_PLUGIN_KEY.getState(state);
        },
      },
    })];
  },
});

const TextAlignment = Extension.create({
  name: "textAlignment",
  addGlobalAttributes() {
    const pageBreakAttribute = {
      default: null,
      parseHTML: (element) => {
        const breakBefore = (element.style.breakBefore || element.style.pageBreakBefore || "").toLowerCase();
        return element.classList.contains(SERIALIZED_PAGE_BREAK_CLASS)
          || breakBefore === "page"
          || breakBefore === "always"
          ? true
          : null;
      },
      renderHTML: (attributes) => (attributes.pageBreakBefore ? { class: SERIALIZED_PAGE_BREAK_CLASS } : {}),
    };
    return [
      {
        types: ["heading", "paragraph"],
        attributes: {
          textAlign: {
            default: null,
            parseHTML: (element) => {
              const value = (element.style.textAlign || element.getAttribute("align") || "").toLowerCase();
              return TEXT_ALIGN_VALUES.includes(value) && value !== "left" ? value : null;
            },
            renderHTML: (attributes) => {
              if (!attributes.textAlign) return {};
              return { style: `text-align: ${attributes.textAlign}` };
            },
          },
          pageBreakBefore: pageBreakAttribute,
        },
      },
      {
        types: ["table", "tableRow"],
        attributes: {
          pageBreakBefore: pageBreakAttribute,
        },
      },
    ];
  },
});

function jsonNodeText(node) {
  if (!node || typeof node !== "object") return "";
  if (node.type === "text") return node.text || "";
  return Array.isArray(node.content) ? node.content.map(jsonNodeText).join("") : "";
}

function appendTextNode(node, text) {
  if (!node || typeof node !== "object") return node;
  if (!Array.isArray(node.content)) {
    node.content = [{ type: "text", text }];
    return node;
  }
  const last = node.content[node.content.length - 1];
  if (last?.type === "text") {
    last.text = `${last.text || ""}${text}`;
    return node;
  }
  node.content.push({ type: "text", text });
  return node;
}

function compactTitleText(value) {
  return String(value || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, "");
}

function hasTemplateVariable(value) {
  return /\{\{[^}]+\}\}/.test(String(value || ""));
}

function placeholder(variable) {
  return "{{" + variable + "}}";
}

function stripTemplateVariables(value) {
  return String(value || "").replace(/\s*\{\{[^}]+\}\}/g, "");
}

function tableLabelVariableRule(text) {
  const compact = compactTitleText(stripTemplateVariables(text));
  if (!compact) return null;
  return TABLE_LABEL_VARIABLE_RULES.find(([label]) => compact.includes(label)) || null;
}

function relatedTitleKey(candidateKey, titleKey) {
  return candidateKey
    && titleKey
    && candidateKey.length <= 60
    && (candidateKey === titleKey || candidateKey.includes(titleKey) || titleKey.includes(candidateKey));
}

function centeredTitleNode(title) {
  return {
    type: "heading",
    attrs: { level: 2, textAlign: "center" },
    content: [{ type: "text", text: title }],
  };
}

function isStandalonePageTitleText(text) {
  const compact = compactTitleText(text);
  return /^(入职承诺书|承诺书|诚信承诺书|入职声明|承诺函|确认书)$/.test(compact)
    || (/协议$/.test(compact) && !/信息|地址|电话|日期/.test(compact));
}

function isPersonalSignatureText(text) {
  const compact = compactTitleText(text);
  return /(本人|员工|乙方|填表人|承诺人).{0,8}(签名|签字|签署|捺印)[:：]?$/.test(compact)
    || /(签名|签字|签署|捺印).{0,8}(本人|员工|乙方|填表人|承诺人)[:：]?$/.test(compact);
}

function centeredPageBreakTitleNode(title) {
  return {
    type: "heading",
    attrs: { level: 2, textAlign: "center", pageBreakBefore: true },
    content: [{ type: "text", text: title }],
  };
}

function normalizeDocumentJsonTitle(documentJson, templateTitle) {
  const title = cleanTemplateName(templateTitle).trim();
  if (!title || !documentJson || typeof documentJson !== "object" || !Array.isArray(documentJson.content)) return documentJson;
  const titleKey = compactTitleText(title);
  const next = JSON.parse(JSON.stringify(documentJson));
  const first = next.content[0];
  const firstTextKey = compactTitleText(jsonNodeText(first));
  if (["heading", "paragraph"].includes(first?.type) && relatedTitleKey(firstTextKey, titleKey)) {
    next.content[0] = centeredTitleNode(jsonNodeText(first).trim() || title);
    return next;
  }
  if (first?.type === "table" && Array.isArray(first.content) && first.content.length) {
    const firstRow = first.content[0];
    if (firstRow?.type === "tableRow" && relatedTitleKey(compactTitleText(jsonNodeText(firstRow)), titleKey)) {
      first.content = first.content.slice(1);
      const rowTitle = jsonNodeText(firstRow).trim() || title;
      next.content = first.content.length
        ? [centeredTitleNode(rowTitle), first, ...next.content.slice(1)]
        : [centeredTitleNode(rowTitle), ...next.content.slice(1)];
      return next;
    }
  }
  // 文件名仅用于模板管理，正文没有同名标题时不得补入，确保删除后不会重新出现。
  return next;
}

function normalizeDocumentJsonPageBreaks(documentJson) {
  if (!documentJson || typeof documentJson !== "object" || !Array.isArray(documentJson.content)) return documentJson;
  const next = JSON.parse(JSON.stringify(documentJson));
  let precedingLength = 0;
  next.content = next.content.map((node) => {
    const nodeText = jsonNodeText(node).trim();
    const shouldBreak = precedingLength > 30 && ["heading", "paragraph"].includes(node?.type) && isStandalonePageTitleText(jsonNodeText(node));
    precedingLength += nodeText.replace(/\s+/g, "").length;
    return shouldBreak ? centeredPageBreakTitleNode(nodeText || "承诺书") : node;
  });
  return next;
}

function normalizeDocumentJsonPersonalSignatureVariables(documentJson) {
  if (!documentJson || typeof documentJson !== "object") return documentJson;
  const next = JSON.parse(JSON.stringify(documentJson));
  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    const text = jsonNodeText(node);
    const canEditNode = ["paragraph", "tableCell", "tableHeader"].includes(node.type);
    const hasNestedBlocks = Array.isArray(node.content) && node.content.some((child) => child?.type && child.type !== "text");
    if (canEditNode && !hasNestedBlocks && isPersonalSignatureText(text) && !hasTemplateVariable(text)) {
      appendTextNode(node, " {{staffName}}");
      return;
    }
    if (Array.isArray(node.content)) node.content.forEach(visit);
  };
  visit(next);
  return next;
}

function removeTemplateVariablesFromJsonNode(node) {
  if (!node || typeof node !== "object") return;
  if (node.type === "text") {
    node.text = stripTemplateVariables(node.text);
    return;
  }
  if (Array.isArray(node.content)) node.content.forEach(removeTemplateVariablesFromJsonNode);
}

function setJsonNodeText(node, text) {
  if (!node || typeof node !== "object") return;
  node.content = [{ type: "paragraph", content: [{ type: "text", text }] }];
}

function normalizeDocumentJsonTableLabelVariables(documentJson) {
  if (!documentJson || typeof documentJson !== "object") return documentJson;
  const next = JSON.parse(JSON.stringify(documentJson));
  const visit = (node) => {
    if (!node || typeof node !== "object") return;
    if (node.type === "tableRow" && Array.isArray(node.content)) {
      for (let index = 0; index < node.content.length - 1; index += 1) {
        const labelCell = node.content[index];
        const valueCell = node.content[index + 1];
        const rule = tableLabelVariableRule(jsonNodeText(labelCell));
        if (!rule) continue;
        const [, variable] = rule;
        const valueText = jsonNodeText(valueCell);
        if (hasTemplateVariable(jsonNodeText(labelCell))) removeTemplateVariablesFromJsonNode(labelCell);
        if (!hasTemplateVariable(valueText)) setJsonNodeText(valueCell, placeholder(variable));
      }
      return;
    }
    if (Array.isArray(node.content)) node.content.forEach(visit);
  };
  visit(next);
  return next;
}

function normalizeDocumentHtmlTitle(html, templateTitle) {
  const title = cleanTemplateName(templateTitle).trim();
  if (!title) return String(html || "");
  const titleKey = compactTitleText(title);
  const makeHeading = (value) => `<h2 style="text-align: center">${value}</h2>`;
  let next = String(html || "");
  const leadingBlockPattern = /^\s*<(h[1-6]|p)\b[^>]*>[\s\S]*?<\/\1>/i;
  const leadingBlock = next.match(leadingBlockPattern)?.[0] || "";
  if (relatedTitleKey(compactTitleText(leadingBlock), titleKey)) {
    return next.replace(leadingBlockPattern, makeHeading(leadingBlock.replace(/<[^>]+>/g, "").trim() || title));
  }
  const leadingTableTitlePattern = /^(\s*)(<table\b[^>]*>\s*)(<tr\b[^>]*>[\s\S]*?<\/tr>)/i;
  const leadingTableTitleMatch = next.match(leadingTableTitlePattern);
  if (leadingTableTitleMatch && relatedTitleKey(compactTitleText(leadingTableTitleMatch[3]), titleKey)) {
    return next.replace(leadingTableTitlePattern, `${makeHeading(leadingTableTitleMatch[3].replace(/<[^>]+>/g, "").trim() || title)}${leadingTableTitleMatch[2]}`);
  }
  // HTML回退与JSON保持一致，只整理已有标题，不把文件名插入正文。
  return next;
}

function normalizeDocumentHtmlPageBreaks(html) {
  let precedingLength = 0;
  return String(html || "").replace(/<(h[1-6]|p)\b[^>]*>[\s\S]*?<\/\1>/gi, (block) => {
    const text = block.replace(/<[^>]+>/g, "").trim();
    const shouldBreak = precedingLength > 30 && isStandalonePageTitleText(text);
    precedingLength += compactTitleText(text).length;
    if (!shouldBreak) return block;
    return `<h2 class="${SERIALIZED_PAGE_BREAK_CLASS}" style="text-align: center">${text || "承诺书"}</h2>`;
  });
}

function normalizeDocumentHtmlPersonalSignatureVariables(html) {
  return String(html || "").replace(/<(p|td|th)\b[^>]*>[\s\S]*?<\/\1>/gi, (block) => {
    const text = block.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
    if (!isPersonalSignatureText(text) || hasTemplateVariable(text)) return block;
    return block.replace(/<\/(p|td|th)>$/i, " {{staffName}}</$1>");
  });
}

function normalizeDocumentHtmlTableLabelVariables(html) {
  return String(html || "").replace(/<tr\b[^>]*>[\s\S]*?<\/tr>/gi, (row) => {
    const cells = [...row.matchAll(/<(td|th)\b[^>]*>[\s\S]*?<\/\1>/gi)];
    if (cells.length < 2) return row;
    const patchedCells = cells.map((cell) => cell[0]);
    for (let index = 0; index < cells.length - 1; index += 1) {
      const labelCell = patchedCells[index];
      const valueCell = patchedCells[index + 1];
      const labelText = labelCell.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
      const rule = tableLabelVariableRule(labelText);
      if (!rule) continue;
      const [, variable] = rule;
      if (hasTemplateVariable(labelText)) {
        patchedCells[index] = labelCell.replace(/(<(?:td|th)\b[^>]*>)([\s\S]*?)(<\/(?:td|th)>)/i, (_, open, body, close) =>
          `${open}${stripTemplateVariables(body)}${close}`,
        );
      }
      if (!hasTemplateVariable(valueCell)) {
        patchedCells[index + 1] = valueCell.replace(/(<(?:td|th)\b[^>]*>)([\s\S]*?)(<\/(?:td|th)>)/i, (_, open, body, close) => {
          const cleanBody = stripTemplateVariables(body);
          const content = cleanBody.replace(/<[^>]+>/g, "").trim() ? `${cleanBody} ${placeholder(variable)}` : placeholder(variable);
          return `${open}${content}${close}`;
        });
      }
    }
    let rowNext = "";
    let cursor = 0;
    cells.forEach((cell, index) => {
      rowNext += row.slice(cursor, cell.index) + patchedCells[index];
      cursor = cell.index + cell[0].length;
    });
    rowNext += row.slice(cursor);
    return rowNext;
  });
}

function parseDocument(version, templateTitle = "") {
  if (version?.documentJson) {
    try {
      const documentJson = typeof version.documentJson === "string" ? JSON.parse(version.documentJson) : version.documentJson;
      return normalizeDocumentJsonPersonalSignatureVariables(normalizeDocumentJsonTableLabelVariables(normalizeDocumentJsonPageBreaks(normalizeDocumentJsonTitle(documentJson, templateTitle))));
    } catch {
      return normalizeDocumentHtmlPersonalSignatureVariables(normalizeDocumentHtmlTableLabelVariables(normalizeDocumentHtmlPageBreaks(normalizeDocumentHtmlTitle(version.editableHtml || "<p></p>", templateTitle))));
    }
  }
  return normalizeDocumentHtmlPersonalSignatureVariables(normalizeDocumentHtmlTableLabelVariables(normalizeDocumentHtmlPageBreaks(normalizeDocumentHtmlTitle(version?.editableHtml || "<p></p>", templateTitle))));
}

function ToolButton({ title, active = false, disabled = false, onClick, children }) {
  return (
    <button
      type="button"
      className={classNames("content-tool-button", active && "is-active")}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function VariablePicker({ disabled, onInsert }) {
  const buttonRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, width: 280, maxHeight: 360 });
  const filteredVariables = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return VARIABLES;
    return VARIABLES.filter(([label, value]) =>
      label.toLowerCase().includes(keyword) || value.toLowerCase().includes(keyword),
    );
  }, [query]);

  function updateMenuPosition() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 280;
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    setMenuPosition({
      top: rect.bottom + 6,
      left,
      width,
      maxHeight: Math.max(220, window.innerHeight - rect.bottom - 18),
    });
  }

  function togglePicker() {
    if (disabled) return;
    setOpen((current) => {
      const next = !current;
      if (next) window.requestAnimationFrame(updateMenuPosition);
      return next;
    });
  }

  function insertVariable(value) {
    onInsert(value);
    setOpen(false);
    setQuery("");
    buttonRef.current?.focus();
  }

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    inputRef.current?.focus();
    function closeOnPointerDown(event) {
      if (buttonRef.current?.contains(event.target)) return;
      if (event.target.closest?.(".content-variable-menu")) return;
      setOpen(false);
    }
    function closeOnEscape(event) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    document.addEventListener("mousedown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
      document.removeEventListener("mousedown", closeOnPointerDown);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={classNames("content-variable-control", open && "is-open")}>
      <button
        ref={buttonRef}
        type="button"
        className="content-variable-trigger"
        title="插入变量"
        aria-label="打开变量库"
        aria-expanded={open}
        disabled={disabled}
        onClick={togglePicker}
      >
        <Variable size={16} aria-hidden="true" />
        <span>变量</span>
      </button>
      {open ? (
        <div
          className="content-variable-menu"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
            width: menuPosition.width,
            maxHeight: menuPosition.maxHeight,
          }}
        >
          <label className="content-variable-search">
            <Search size={14} aria-hidden="true" />
            <span className="sr-only">搜索变量</span>
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索变量名称或字段"
            />
          </label>
          <div className="content-variable-options" role="listbox" aria-label="变量库">
            {filteredVariables.length ? filteredVariables.map(([label, value]) => (
              <button key={value} type="button" role="option" onClick={() => insertVariable(value)}>
                <span>{label}</span>
                <code>{value}</code>
              </button>
            )) : (
              <p className="content-variable-empty">没有匹配变量</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SignaturePageOverlay({
  pageNumber,
  fields = [],
  roles = [],
  selectedFieldId = "",
  placementRoleId = "",
  onFieldSelect,
  onFieldChange,
  onFieldDelete,
  onDropRole,
  onPlaceRole,
  readOnly = false,
}) {
  const layerRef = useRef(null);

  const positionFromPointer = useCallback((event, field, mode = "move", origin = field) => {
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return;
    const dx = ((event.clientX - origin.pointerX) / rect.width) * 100;
    const dy = ((event.clientY - origin.pointerY) / rect.height) * 100;
    if (mode === "resize") {
      const width = Math.max(5, Math.min(40, origin.width + dx));
      const height = field.action === "company-seal"
        ? Math.max(5, Math.min(28, width * rect.width / rect.height))
        : Math.max(3.5, Math.min(18, origin.height + dy));
      onFieldChange?.(field.id, {
        width,
        height,
        x: Math.min(origin.x, 100 - width),
        y: Math.min(origin.y, 100 - height),
      });
      return;
    }
    onFieldChange?.(field.id, {
      x: Math.max(0, Math.min(100 - field.width, origin.x + dx)),
      y: Math.max(0, Math.min(100 - field.height, origin.y + dy)),
    });
  }, [onFieldChange]);

  function startPointer(event, field, mode) {
    if (readOnly) return;
    event.preventDefault();
    event.stopPropagation();
    onFieldSelect?.(field.id);
    const origin = { ...field, pointerX: event.clientX, pointerY: event.clientY };
    const move = (moveEvent) => positionFromPointer(moveEvent, field, mode, origin);
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function drop(event) {
    event.preventDefault();
    if (readOnly) return;
    const roleId = event.dataTransfer.getData("application/x-signature-role");
    const rect = layerRef.current?.getBoundingClientRect();
    if (!roleId || !rect) return;
    onDropRole?.(roleId, pageNumber, ((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100);
  }

  function place(event) {
    if (readOnly || !placementRoleId || event.target !== event.currentTarget) return;
    const rect = layerRef.current?.getBoundingClientRect();
    if (!rect) return;
    onPlaceRole?.(placementRoleId, pageNumber, ((event.clientX - rect.left) / rect.width) * 100, ((event.clientY - rect.top) / rect.height) * 100);
  }

  return (
    <div
      className={classNames("signature-field-layer", placementRoleId && !readOnly && "is-placing")}
      ref={layerRef}
      onDragOver={(event) => { if (!readOnly) event.preventDefault(); }}
      onDrop={drop}
      onClick={place}
      aria-label={`正文预览第 ${pageNumber} 页签章层`}
    >
      {fields.map((field) => {
        const role = roles.find((item) => item.id === field.roleId);
        const editableField = field.action ? field : { ...field, action: role?.action };
        return (
          <div
            className={classNames(
              "signature-field-marker",
              editableField.action === "company-seal" && "is-company-seal",
              field.suggestion && "is-suggestion",
              selectedFieldId === field.id && "is-selected",
            )}
            key={field.id}
            style={{ left: `${field.x}%`, top: `${field.y}%`, width: `${field.width}%`, height: `${field.height}%` }}
            onPointerDown={(event) => startPointer(event, editableField, "move")}
            aria-label={`${role?.label || "签署方"}，第 ${pageNumber} 页签章位置`}
          >
            <span>{role?.label || "签署方"}</span>
            {!readOnly ? <button type="button" className="field-delete" title="删除签章位置" aria-label="删除签章位置" onPointerDown={(event) => { event.preventDefault(); event.stopPropagation(); }} onClick={(event) => { event.stopPropagation(); onFieldDelete?.(field.id); }}><Trash2 size={12} /></button> : null}
            {!readOnly ? <button type="button" className="field-resize" title="调整大小" aria-label="调整签章位置大小" onPointerDown={(event) => startPointer(event, editableField, "resize")}><GripVertical size={12} /></button> : null}
          </div>
        );
      })}
    </div>
  );
}

const TemplateContentEditor = forwardRef(function TemplateContentEditor({
  version,
  readOnly,
  onDirtyChange,
  onPageCountChange,
  preview = false,
  signatureOverlay = null,
  templateTitle = "",
}, ref) {
  const imageInputRef = useRef(null);
  const paperRef = useRef(null);
  const paginationFrameRef = useRef(0);
  const editorInstanceRef = useRef(null);
  const syncingRef = useRef(false);
  const baselineRef = useRef("");
  const paginationBreaksRef = useRef([]);
  const [pagination, setPagination] = useState(() => initialPagination(version?.pageCount));

  const paginateEditor = useCallback(() => {
    const paper = paperRef.current;
    const currentEditor = editorInstanceRef.current;
    if (!paper || !currentEditor || currentEditor.isDestroyed) return;
    let view;
    try {
      view = currentEditor.view;
    } catch {
      return;
    }
    const content = view.dom;
    if (!paper.contains(content)) return;

    view.dispatch(
      currentEditor.state.tr
        .setMeta(PAGINATION_PLUGIN_KEY, { breaks: [] })
        .setMeta("addToHistory", false),
    );
    const paperWidth = paper.getBoundingClientRect().width;
    if (paperWidth <= 0) return;

    const contentStyle = window.getComputedStyle(content);
    const paperStyle = window.getComputedStyle(paper);
    const pageHeight = paperWidth * A4_HEIGHT_RATIO;
    const pageGap = Number.parseFloat(paperStyle.getPropertyValue("--template-content-page-gap")) || DEFAULT_EDITOR_PAGE_GAP;
    const paddingTop = Number.parseFloat(contentStyle.paddingTop) || 0;
    const paddingBottom = Number.parseFloat(contentStyle.paddingBottom) || 0;
    const contentHeight = Math.max(1, pageHeight - paddingTop - paddingBottom);
    const contentRect = content.getBoundingClientRect();
    const pageStride = pageHeight + pageGap;
    const blocks = [];
    const appendMeasuredBlock = (node, position, kind = "node") => {
      const element = view.nodeDOM(position);
      if (!(element instanceof HTMLElement)) return false;
      const rect = element.getBoundingClientRect();
      blocks.push({
        element,
        kind,
        position,
        type: node.type.name,
        text: node.textContent || "",
        pageBreakBefore: Boolean(node.attrs?.pageBreakBefore),
        top: rect.top - contentRect.top,
        bottom: rect.bottom - contentRect.top,
        height: rect.height,
        marginTop: Number.parseFloat(window.getComputedStyle(element).marginTop) || 0,
      });
      return true;
    };
    currentEditor.state.doc.forEach((node, position) => {
      const element = view.nodeDOM(position);
      const rect = element instanceof HTMLElement ? element.getBoundingClientRect() : null;
      if (node.type.name === "table" && rect?.height > contentHeight + 1) {
        node.forEach((row, rowOffset) => {
          appendMeasuredBlock(row, position + 1 + rowOffset, "tableRow");
        });
        return;
      }
      appendMeasuredBlock(node, position);
    });

    let pageIndex = 0;
    let addedOffset = 0;
    let finalBottom = paddingTop;
    const pageBreaks = [];
    blocks.forEach((block, index) => {
      let adjustedTop = block.top + addedOffset;
      let adjustedBottom = block.bottom + addedOffset;
      const canMoveAsBlock = block.height <= contentHeight + 1;
      let pageTop = pageIndex * (pageHeight + pageGap);
      let pageContentTop = pageTop + paddingTop;
      let pageContentBottom = pageTop + pageHeight - paddingBottom;

      const syncPageIndex = (nextPageIndex) => {
        pageIndex = Math.max(pageIndex, nextPageIndex);
        pageTop = pageIndex * pageStride;
        pageContentTop = pageTop + paddingTop;
        pageContentBottom = pageTop + pageHeight - paddingBottom;
      };

      const addPageBreak = (breakTarget, breakTop, topGap = 0, targetPageIndex = pageIndex + 1) => {
        const targetContentTop = targetPageIndex * pageStride + paddingTop + topGap;
        const breakOffset = Math.max(0, targetContentTop - breakTop);
        if (breakOffset <= 0.5) {
          syncPageIndex(targetPageIndex);
          return false;
        }
        pageBreaks.push(breakTarget.kind === "tableRow"
          ? {
              kind: "tableRow",
              position: breakTarget.position,
              height: breakOffset,
              gapStart: Math.max(0, pageTop + pageHeight - breakTop),
              pageGap,
            }
          : { kind: "node", position: breakTarget.position, marginTop: breakTarget.marginTop + breakOffset });
        addedOffset += breakOffset;
        adjustedTop += breakOffset;
        adjustedBottom += breakOffset;
        syncPageIndex(targetPageIndex);
        return true;
      };

      const hardBreakTarget = block.pageBreakBefore ? block : null;
      if (canMoveAsBlock && hardBreakTarget && adjustedTop > pageContentTop + 0.5) {
        const breakTop = hardBreakTarget.top + addedOffset;
        const targetPageIndex = hardPageBreakTargetPageIndex({
          blocks,
          index,
          pageIndex,
          pageStride,
          pageHeight,
          paddingTop,
          paddingBottom,
          adjustedTop,
          breakTop,
          addedOffset,
        });
        addPageBreak(hardBreakTarget, breakTop, HARD_PAGE_BREAK_TOP_GAP, targetPageIndex);
      }

      if (canMoveAsBlock && adjustedBottom > pageContentBottom + 0.5 && adjustedTop > pageContentTop + 0.5) {
        const breakTarget = headingGroupBreakTarget(blocks, index, addedOffset, pageContentTop);
        const breakTop = breakTarget.top + addedOffset;
        addPageBreak(breakTarget, breakTop);
      }
      finalBottom = Math.max(finalBottom, adjustedBottom);
    });

    view.dispatch(
      currentEditor.state.tr
        .setMeta(PAGINATION_PLUGIN_KEY, { breaks: pageBreaks })
        .setMeta("addToHistory", false),
    );

    while (finalBottom > pageIndex * pageStride + pageHeight - paddingBottom + 0.5) {
      pageIndex += 1;
    }
    const count = Math.max(1, pageIndex + 1);
    const height = count * pageHeight + (count - 1) * pageGap;
    paper.dataset.paginationReady = "true";
    paper.dataset.pageBreakCount = String(pageBreaks.length);
    paginationBreaksRef.current = pageBreaks;
    setPagination((current) => (
      current.count === count && Math.abs(current.height - height) < 0.5
        && samePageBreaks(current.breaks || [], pageBreaks)
        ? current
        : { count, height, breaks: pageBreaks }
    ));
  }, []);

  const schedulePagination = useCallback(() => {
    window.cancelAnimationFrame(paginationFrameRef.current);
    paginationFrameRef.current = window.requestAnimationFrame(() => {
      paginationFrameRef.current = 0;
      paginateEditor();
    });
  }, [paginateEditor]);

  const editor = useEditor({
    extensions: [
      // 不在签署表格后自动补空段落，避免末页溢出并将空白页写入正式分页。
      StarterKit.configure({ link: false, underline: false, trailingNode: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: false, protocols: [] }),
      TextAlignment,
      PaginationDecorations,
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image.configure({ allowBase64: true, inline: false }),
    ],
    content: parseDocument(version, templateTitle),
    editable: !readOnly,
    onCreate: () => {
      // 实例引用由挂载effect统一维护，过期实例的延迟回调不能覆盖当前分页实例。
      schedulePagination();
    },
    onUpdate: ({ editor: currentEditor }) => {
      if (currentEditor.isDestroyed) return;
      if (!syncingRef.current) {
        onDirtyChange?.(JSON.stringify(currentEditor.getJSON()) !== baselineRef.current);
      }
      schedulePagination();
    },
  });

  useEffect(() => {
    if (!editor) return;
    editorInstanceRef.current = editor;
    editor.setEditable(!readOnly);
    return () => {
      if (editorInstanceRef.current === editor) editorInstanceRef.current = null;
    };
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor || !version?.id) return;
    syncingRef.current = true;
    paginationBreaksRef.current = [];
    setPagination(initialPagination(version.pageCount));
    editor.commands.setContent(parseDocument(version, templateTitle), { emitUpdate: false, errorOnInvalidContent: false });
    baselineRef.current = JSON.stringify(editor.getJSON());
    onDirtyChange?.(false);
    schedulePagination();
    const release = window.setTimeout(() => {
      syncingRef.current = false;
      onDirtyChange?.(false);
    }, 0);
    return () => window.clearTimeout(release);
  }, [editor, onDirtyChange, schedulePagination, templateTitle, version?.id, version?.lockVersion, version?.contentSha256, version?.pageCount]);

  useEffect(() => {
    if (!editor) return undefined;
    const paper = paperRef.current;
    const content = paper?.querySelector(".tiptap");
    if (!paper || !content) return undefined;

    let measuredWidth = 0;
    const resizeObserver = new ResizeObserver(() => {
      const nextWidth = paper.getBoundingClientRect().width;
      if (Math.abs(nextWidth - measuredWidth) < 0.5) return;
      measuredWidth = nextWidth;
      schedulePagination();
    });
    const repaginateAfterAssetLoad = () => schedulePagination();
    resizeObserver.observe(paper);
    content.addEventListener("load", repaginateAfterAssetLoad, true);
    document.fonts?.ready.then(schedulePagination).catch(() => {});
    schedulePagination();
    return () => {
      resizeObserver.disconnect();
      content.removeEventListener("load", repaginateAfterAssetLoad, true);
      window.cancelAnimationFrame(paginationFrameRef.current);
    };
  }, [editor, schedulePagination]);

  useEffect(() => {
    onPageCountChange?.(pagination.count);
  }, [onPageCountChange, pagination.count]);

  useImperativeHandle(ref, () => ({
    getContent() {
      paginateEditor();
      const documentJson = editor ? withSerializedPageBreaks(editor.getJSON(), paginationBreaksRef.current) : null;
      return { documentJson, editableHtml: editor?.getHTML() || "" };
    },
    focus() {
      editor?.commands.focus();
    },
  }), [editor, paginateEditor]);

  function setLink() {
    if (!editor || readOnly) return;
    const previous = editor.getAttributes("link").href || "";
    const href = window.prompt("输入文内锚点，例如 #section-1", previous || "#");
    if (href === null) return;
    if (!href.trim()) editor.chain().focus().unsetLink().run();
    else editor.chain().focus().extendMarkRange("link").setLink({ href: href.trim() }).run();
  }

  function addImage(file) {
    if (!file || !editor) return;
    if (!/^image\/(png|jpeg|gif|webp)$/i.test(file.type) || file.size > 2 * 1024 * 1024) {
      window.alert("仅支持 2 MB 以内的 PNG、JPG、GIF 或 WebP 图片");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => editor.chain().focus().setImage({ src: String(reader.result || ""), alt: file.name }).run();
    reader.readAsDataURL(file);
  }

  if (!editor) return <div className="template-content-loading">正在初始化正文编辑器</div>;
  const inTable = editor.isActive("table");
  const activeTextBlock = editor.isActive("heading") ? "heading" : "paragraph";
  const activeTextAlign = editor.getAttributes(activeTextBlock).textAlign || "left";
  const savedPageCount = positivePageCount(version?.pageCount);
  const pageCountMismatch = pagination.count !== savedPageCount;

  function setTextAlign(alignment) {
    if (!editor || readOnly) return;
    const type = editor.isActive("heading") ? "heading" : "paragraph";
    editor.chain().focus().updateAttributes(type, { textAlign: alignment === "left" ? null : alignment }).run();
  }

  return (
    <section className={classNames("template-content-editor", preview && "is-preview")} aria-label={preview ? "模板正文预览" : "模板正文编辑器"}>
      {!preview ? <div className="template-content-toolbar" role="toolbar" aria-label="正文格式">
        <ToolButton title="正文段落" active={editor.isActive("paragraph")} disabled={readOnly} onClick={() => editor.chain().focus().setParagraph().run()}><Pilcrow size={16} /></ToolButton>
        <ToolButton title="一级标题" active={editor.isActive("heading", { level: 1 })} disabled={readOnly} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={16} /></ToolButton>
        <ToolButton title="二级标题" active={editor.isActive("heading", { level: 2 })} disabled={readOnly} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16} /></ToolButton>
        <span className="content-tool-separator" />
        <ToolButton title="左对齐" active={activeTextAlign === "left"} disabled={readOnly} onClick={() => setTextAlign("left")}><AlignLeft size={16} /></ToolButton>
        <ToolButton title="居中" active={activeTextAlign === "center"} disabled={readOnly} onClick={() => setTextAlign("center")}><AlignCenter size={16} /></ToolButton>
        <ToolButton title="右对齐" active={activeTextAlign === "right"} disabled={readOnly} onClick={() => setTextAlign("right")}><AlignRight size={16} /></ToolButton>
        <span className="content-tool-separator" />
        <ToolButton title="加粗" active={editor.isActive("bold")} disabled={readOnly} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={16} /></ToolButton>
        <ToolButton title="斜体" active={editor.isActive("italic")} disabled={readOnly} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={16} /></ToolButton>
        <ToolButton title="下划线" active={editor.isActive("underline")} disabled={readOnly} onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon size={16} /></ToolButton>
        <ToolButton title="删除线" active={editor.isActive("strike")} disabled={readOnly} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={16} /></ToolButton>
        <span className="content-tool-separator" />
        <ToolButton title="无序列表" active={editor.isActive("bulletList")} disabled={readOnly} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={16} /></ToolButton>
        <ToolButton title="有序列表" active={editor.isActive("orderedList")} disabled={readOnly} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={16} /></ToolButton>
        <ToolButton title="文内链接" active={editor.isActive("link")} disabled={readOnly} onClick={setLink}><Link2 size={16} /></ToolButton>
        <ToolButton title="插入图片" disabled={readOnly} onClick={() => imageInputRef.current?.click()}><ImagePlus size={16} /></ToolButton>
        <input ref={imageInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={(event) => { addImage(event.target.files?.[0]); event.target.value = ""; }} />
        <VariablePicker disabled={readOnly} onInsert={(value) => editor.chain().focus().insertContent(value).run()} />
        <span className="content-tool-separator" />
        <ToolButton title="插入表格" disabled={readOnly} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><Table2 size={16} /></ToolButton>
        <ToolButton title="增加一行" disabled={readOnly || !inTable} onClick={() => editor.chain().focus().addRowAfter().run()}><Rows3 size={16} /><Plus size={10} /></ToolButton>
        <ToolButton title="删除一行" disabled={readOnly || !inTable} onClick={() => editor.chain().focus().deleteRow().run()}><Rows3 size={16} /><Minus size={10} /></ToolButton>
        <ToolButton title="增加一列" disabled={readOnly || !inTable} onClick={() => editor.chain().focus().addColumnAfter().run()}><Columns3 size={16} /><Plus size={10} /></ToolButton>
        <ToolButton title="删除一列" disabled={readOnly || !inTable} onClick={() => editor.chain().focus().deleteColumn().run()}><Columns3 size={16} /><Minus size={10} /></ToolButton>
        <ToolButton title="删除表格" disabled={readOnly || !inTable} onClick={() => editor.chain().focus().deleteTable().run()}><Trash2 size={16} /></ToolButton>
        <span className="content-tool-separator" />
        <ToolButton title="撤销" disabled={readOnly || !editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={16} /></ToolButton>
        <ToolButton title="重做" disabled={readOnly || !editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={16} /></ToolButton>
      </div> : null}
      {!preview && pageCountMismatch ? (
        <div className="template-content-pagination-warning">
          编辑分页 {pagination.count} 页，当前版本 PDF {savedPageCount} 页；{version?.status === "published" ? "保存为新草稿后再预览/签章。" : "点击顶部“刷新版式 PDF”后再预览/签章。"}
        </div>
      ) : null}
      <div className="template-content-scroll">
        <div
          ref={paperRef}
          className="template-content-paper"
          data-page-count={pagination.count}
          style={{ "--template-content-editor-height": `${pagination.height}px` }}
        >
          <div className="template-content-pages" aria-hidden="true">
            {Array.from({ length: pagination.count }, (_, index) => (
              <div className="template-content-page" key={index + 1}>
                <span>{index + 1} / {pagination.count}</span>
              </div>
            ))}
          </div>
          <EditorContent editor={editor} className="template-content-flow" />
          {signatureOverlay ? (
            <div className="template-content-signature-pages">
              {Array.from({ length: pagination.count }, (_, index) => {
                const pageNumber = index + 1;
                return (
                  <div className="template-content-signature-page" key={pageNumber}>
                    <SignaturePageOverlay
                      {...signatureOverlay}
                      pageNumber={pageNumber}
                      fields={(signatureOverlay.fields || []).filter((field) => Number(field.page) === pageNumber)}
                    />
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
});

export default TemplateContentEditor;
